import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AxiosError } from 'axios';
import {
  postApiV1AuthOtpRequest,
  postApiV1AuthOtpVerify,
} from '@/shared/api/generated/auth/auth';
import { rolesIn, SystemRoles } from '@/features/auth/model/use-roles';
import { writeSession } from '@/shared/api/session';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

/**
 * OTP sign-in (§23.8: the only way into the system).
 *
 * Two steps in one component because they are one decision for the operator: the number
 * they typed is still on screen when the code arrives, and a route change between the
 * steps would lose it.
 */
const phoneSchema = z
  .string()
  .trim()
  // Matches the canonicalisation the server does (PhoneNumber value object): local,
  // international, and 00-prefixed forms all reach the same account. Validated here only
  // to give an immediate message — the server re-validates regardless.
  .regex(/^(?:\+?966|00966|0)?5\d{8}$/);

const codeSchema = z.string().trim().regex(/^\d{6}$/);

type Step = 'phone' | 'code';

export function SignInForm() {
  const { t } = useLocale();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const phoneForm = useForm<{ phone: string }>({ defaultValues: { phone: '' } });
  const codeForm = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const describeFailure = (cause: unknown): string => {
    if (cause instanceof AxiosError) {
      if (cause.response?.status === 429) {
        return t.signIn.tooMany;
      }
      if (cause.response?.status === 401) {
        return t.signIn.rejected;
      }
      if (cause.response?.status === 400) {
        return t.signIn.invalidPhone;
      }
    }

    return t.signIn.failed;
  };

  const requestCode = phoneForm.handleSubmit(async ({ phone: entered }) => {
    setError(null);

    const parsed = phoneSchema.safeParse(entered);
    if (!parsed.success) {
      setError(t.signIn.invalidPhone);
      return;
    }

    try {
      await postApiV1AuthOtpRequest({ phone: parsed.data });

      setPhone(parsed.data);
      setStep('code');

      // Worded to cover both outcomes, because the endpoint answers identically for a
      // registered and an unregistered number — deliberately, so it cannot be used to
      // discover who is an Aoun volunteer. Promising "a code has been sent" would be a
      // claim the API does not make.
      setNotice(t.signIn.codeSent);
    } catch (cause) {
      setError(describeFailure(cause));
    }
  });

  const verifyCode = codeForm.handleSubmit(async ({ code }) => {
    setError(null);

    const parsed = codeSchema.safeParse(code);
    if (!parsed.success) {
      setError(t.signIn.invalidCode);
      return;
    }

    try {
      const tokens = await postApiV1AuthOtpVerify({ phone, code: parsed.data });

      // ⚠️ **The dashboard is for the association's administration, and says so at the door.**
      //
      // The association's decision: the committee, desk control and the head of control all work
      // from the app. Until 2 September 2026 any of them could sign in here and were handed ten
      // screens — volunteers, permissions, the points table, the audit trail — every one of which
      // answered «لا تملك صلاحية هذا الإجراء». Measured on the panel with a head of control.
      //
      // ⚠️ **An affordance, not a security boundary**, and it must not be mistaken for one: the
      // role is read from an unverified token, and the whole reason that is acceptable is that
      // getting past it wins nothing — every request is authorised by policy on the server
      // (§12.2), which is what actually refused all ten screens.
      //
      // ⚠️ The session is not written, so nothing is stored for a person who cannot use this. The
      // tokens they were issued are still perfectly good — in the app, which is where they belong.
      if (!rolesIn(tokens.accessToken).includes(SystemRoles.SystemAdministrator)) {
        setError(t.signIn.notForYou);
        return;
      }

      writeSession({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      });
    } catch (cause) {
      setError(describeFailure(cause));
    }
  });

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">{t.signIn.heading}</h1>
        <p className="text-muted-foreground">{t.signIn.lede}</p>
      </header>

      {step === 'phone' ? (
        <form onSubmit={requestCode} className="flex flex-col gap-4" noValidate>
          <Field label={t.signIn.phoneLabel} htmlFor="phone">
            <input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              placeholder={t.signIn.phonePlaceholder}
              className="tabular w-full rounded-md border border-border bg-surface px-3 py-2 text-start outline-none focus:border-primary"
              {...phoneForm.register('phone')}
            />
          </Field>

          <Button type="submit" disabled={phoneForm.formState.isSubmitting}>
            {t.signIn.requestCode}
          </Button>
        </form>
      ) : (
        <form onSubmit={verifyCode} className="flex flex-col gap-4" noValidate>
          {notice ? <p className="text-sm text-muted-foreground">{notice}</p> : null}

          <Field label={t.signIn.codeLabel} htmlFor="code">
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={6}
              placeholder={t.signIn.codePlaceholder}
              className="tabular w-full rounded-md border border-border bg-surface px-3 py-2 text-center text-2xl tracking-[0.4em] outline-none focus:border-primary"
              {...codeForm.register('code')}
            />
          </Field>

          <Button type="submit" disabled={codeForm.formState.isSubmitting}>
            {t.signIn.verify}
          </Button>

          <button
            type="button"
            className="text-sm text-muted-foreground underline underline-offset-4"
            onClick={() => {
              setStep('phone');
              setError(null);
              setNotice(null);
            }}
          >
            {t.signIn.changeNumber}
          </button>
        </form>
      )}

      {error ? (
        <p role="alert" className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
